import { COLORS, baseStyles } from "../shared/constants";
import { useState, useEffect, useCallback } from "react";
import * as api from "../api";
import DevicePairing from "./DevicePairing";

// ─── Login Screen — two-tier (operator picker → PIN pad) ────
//
// Phase 1: load operators from /api/auth/users (requires device-creds
//          in permissive mode the backend returns the list without
//          them). Render as a grid of cards, MD first.
// Phase 2: tap an operator → PIN pad with the operator's name shown
//          and a back arrow to return to the picker.
//
// Legacy fallback: if /api/auth/users fails or returns empty, render
// the old PIN-only pad. This covers the rollout window where the
// frontend deploys before backend T4, or any unexpected backend state.
//
// On 6-digit submit:
//   - If an operator is selected → api.login(user_id, pin)
//   - If legacy fallback        → api.login(pin)
// Both call shapes are accepted by the backend during permissive mode.

export default function LoginScreen({ onLogin }) {
  const [phase, setPhase] = useState("loading");          // 'loading' | 'pair' | 'picker' | 'pin' | 'legacy'
  const [operators, setOperators] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null); // {id, first_name, last_name, role}
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [attempts, setAttempts] = useState(0);
  const [locked, setLocked] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);

  // ── Load operators on mount AND on demand (after a successful pair) ──
  const loadOperators = useCallback(async () => {
    setPhase("loading");
    try {
      const data = await api.getOperators();
      const list = (data && data.users) || [];
      if (list.length === 0) {
        setPhase("legacy");
        return;
      }
      setOperators(list);
      setPhase("picker");
    } catch (e) {
      // Backend says this iPad must be paired first — switch to pair UI.
      // The error shape comes from app.utils.device_auth.device_required:
      //   { status: 401, error: "device_required", detail: "..." }
      if (e && e.status === 401 && e.error === "device_required") {
        setPhase("pair");
        return;
      }
      // 404 (backend pre-T4) or network — fall back to legacy
      // direct-PIN entry so a clinic doesn't get locked out.
      setPhase("legacy");
    }
  }, []);

  useEffect(() => {
    loadOperators();
  }, [loadOperators]);

  // ── Submit PIN ───────────────────────────────────────────────
  const submitPin = async (newPin) => {
    setLoggingIn(true);
    try {
      const data = selectedUser
        ? await api.login(selectedUser.id, newPin)
        : await api.login(newPin);
      setTimeout(() => onLogin(data.user), 200);
    } catch (e) {
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      if (e.status === 423 || newAttempts >= 5) {
        setLocked(true);
        setError("Account locked. Contact administrator.");
      } else {
        setError(e.error || `Invalid PIN. ${5 - newAttempts} attempts remaining.`);
      }
      setTimeout(() => setPin(""), 300);
      setLoggingIn(false);
    }
  };

  const handlePinEntry = (digit) => {
    if (locked || loggingIn) return;
    if (pin.length >= 6) return;
    const newPin = pin + digit;
    setPin(newPin);
    setError("");
    if (newPin.length === 6) {
      submitPin(newPin);
    }
  };

  const handleBackspace = () => {
    setPin((p) => p.slice(0, -1));
    setError("");
  };

  // ── Keyboard support on the PIN pad ──────────────────────────
  const handleKeyDown = useCallback(
    (e) => {
      if (phase !== "pin" && phase !== "legacy") return;
      if (e.key >= "0" && e.key <= "9") handlePinEntry(e.key);
      else if (e.key === "Backspace") handleBackspace();
      else if (e.key === "Escape" && phase === "pin") backToPicker();
    },
    [pin, locked, loggingIn, attempts, phase, selectedUser],
  );
  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  const pickOperator = (user) => {
    setSelectedUser(user);
    setPin("");
    setError("");
    setAttempts(0);
    setLocked(false);
    setPhase("pin");
  };

  const backToPicker = () => {
    setSelectedUser(null);
    setPin("");
    setError("");
    setAttempts(0);
    setLocked(false);
    setPhase(operators.length > 0 ? "picker" : "legacy");
  };

  // ─────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────
  const wrapperStyle = {
    ...baseStyles,
    background: `linear-gradient(145deg, ${COLORS.primary} 0%, #0F3D52 100%)`,
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
  };

  const headerBlock = (
    <div style={{ textAlign: "center", marginBottom: "40px" }}>
      <h1 style={{ color: "#FFFFFF", fontSize: "28px", fontWeight: "700", letterSpacing: "0.5px", margin: 0 }}>
        Fox TMS Clinic
      </h1>
      <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "14px", marginTop: "8px", letterSpacing: "1px", textTransform: "uppercase" }}>
        Clinical EHR System
      </p>
    </div>
  );

  // Phase: loading
  if (phase === "loading") {
    return (
      <div style={wrapperStyle}>
        {headerBlock}
        <p style={{ color: "rgba(255,255,255,0.7)", fontSize: "14px" }}>Loading…</p>
      </div>
    );
  }

  // Phase: device pairing — first-time iPad setup, or admin-triggered re-pair
  if (phase === "pair") {
    return (
      <DevicePairing
        onPaired={() => loadOperators()}
        // "Back" cancels and falls through to legacy PIN. Useful in
        // permissive mode where the operator may not have a code yet
        // and just wants to log in.
        onCancel={() => setPhase(operators.length > 0 ? "picker" : "legacy")}
      />
    );
  }

  // Phase: operator picker
  if (phase === "picker") {
    return (
      <div style={wrapperStyle}>
        {headerBlock}
        <div style={{
          background: COLORS.surface,
          borderRadius: "16px",
          padding: "32px 28px",
          width: "100%",
          maxWidth: "520px",
          boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
        }}>
          <p style={{
            textAlign: "center", color: COLORS.textSecondary,
            fontSize: "15px", marginBottom: "20px", fontWeight: "500",
          }}>
            Who's working?
          </p>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
            gap: "12px",
          }}>
            {operators.map((u) => (
              <button
                key={u.id}
                onClick={() => pickOperator(u)}
                style={{
                  padding: "16px 12px",
                  borderRadius: "12px",
                  border: "none",
                  background: COLORS.inputBg,
                  cursor: "pointer",
                  textAlign: "center",
                  transition: "background 0.15s, transform 0.05s",
                }}
                onMouseDown={(e) => { e.currentTarget.style.background = COLORS.border; }}
                onMouseUp={(e) => { e.currentTarget.style.background = COLORS.inputBg; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = COLORS.inputBg; }}
              >
                <div style={{
                  fontSize: "16px",
                  fontWeight: "600",
                  color: COLORS.text,
                  marginBottom: "4px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}>
                  {u.first_name} {u.last_name}
                </div>
                <div style={{
                  fontSize: "11px",
                  fontWeight: "600",
                  color: u.role === "MD" ? COLORS.primary : COLORS.textSecondary,
                  letterSpacing: "0.6px",
                  textTransform: "uppercase",
                }}>
                  {u.role}
                </div>
              </button>
            ))}
          </div>
          <div style={{ textAlign: "center", marginTop: "20px" }}>
            <button
              onClick={() => setPhase("pair")}
              style={{
                background: "transparent",
                border: "none",
                color: COLORS.textSecondary,
                fontSize: "12px",
                fontWeight: "500",
                cursor: "pointer",
                padding: "6px 10px",
                textDecoration: "underline",
              }}
            >
              Pair this iPad
            </button>
          </div>
          <p style={{ textAlign: "center", color: COLORS.textSecondary, fontSize: "11px", marginTop: "12px" }}>
            204 N Cedar St, Ste 100 · Summerville, SC
          </p>
        </div>
      </div>
    );
  }

  // Phase: PIN pad (either with selected operator OR legacy fallback)
  const isLegacy = phase === "legacy";
  return (
    <div style={wrapperStyle}>
      {headerBlock}
      <div style={{
        background: COLORS.surface,
        borderRadius: "16px",
        padding: "40px 36px",
        width: "100%",
        maxWidth: "360px",
        boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
        position: "relative",
      }}>
        {/* Back arrow — only when an operator was picked (not in legacy fallback) */}
        {!isLegacy && (
          <button
            onClick={backToPicker}
            style={{
              position: "absolute",
              top: "16px",
              left: "16px",
              background: "transparent",
              border: "none",
              fontSize: "20px",
              color: COLORS.textSecondary,
              cursor: "pointer",
              padding: "4px 8px",
            }}
            aria-label="Back to operator picker"
          >
            ‹ Back
          </button>
        )}

        {selectedUser && (
          <p style={{
            textAlign: "center",
            color: COLORS.text,
            fontSize: "18px",
            fontWeight: "600",
            margin: "0 0 4px 0",
          }}>
            {selectedUser.first_name} {selectedUser.last_name}
          </p>
        )}
        <p style={{
          textAlign: "center", color: COLORS.textSecondary,
          fontSize: "14px", marginBottom: "24px", fontWeight: "500",
        }}>
          Enter your 6-digit PIN
        </p>

        {/* PIN dots */}
        <div style={{ display: "flex", justifyContent: "center", gap: "14px", marginBottom: "28px" }}>
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} style={{
              width: "16px",
              height: "16px",
              borderRadius: "50%",
              background: i < pin.length ? COLORS.primary : "transparent",
              border: `2px solid ${i < pin.length ? COLORS.primary : COLORS.border}`,
              transition: "all 0.15s ease",
              transform: i < pin.length ? "scale(1.1)" : "scale(1)",
            }} />
          ))}
        </div>

        {error && (
          <p style={{
            textAlign: "center",
            color: COLORS.danger,
            fontSize: "13px",
            marginBottom: "16px",
            fontWeight: "500",
          }}>
            {error}
          </p>
        )}

        {/* Number pad */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "10px" }}>
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, null, 0, "⌫"].map((key, idx) => (
            <button
              key={idx}
              onClick={() => {
                if (key === "⌫") handleBackspace();
                else if (key !== null) handlePinEntry(String(key));
              }}
              disabled={locked || key === null}
              style={{
                height: "60px",
                borderRadius: "12px",
                border: "none",
                background: key === null ? "transparent" : COLORS.inputBg,
                color: COLORS.text,
                fontSize: key === "⌫" ? "20px" : "22px",
                fontWeight: "600",
                cursor: key === null || locked ? "default" : "pointer",
                opacity: key === null ? 0 : locked ? 0.4 : 1,
                transition: "background 0.15s",
              }}
              onMouseDown={(e) => {
                if (key !== null && !locked) e.currentTarget.style.background = COLORS.border;
              }}
              onMouseUp={(e) => {
                if (key !== null && !locked) e.currentTarget.style.background = COLORS.inputBg;
              }}
              onMouseLeave={(e) => {
                if (key !== null && !locked) e.currentTarget.style.background = COLORS.inputBg;
              }}
            >
              {key}
            </button>
          ))}
        </div>

        <p style={{ textAlign: "center", color: COLORS.textSecondary, fontSize: "11px", marginTop: "24px" }}>
          204 N Cedar St, Ste 100 · Summerville, SC
        </p>
      </div>
    </div>
  );
}

// ─── Header ──────────────────────────────────────────────────
